import Cookies from 'js-cookie';
import { isBeforeDate } from '~/helpers/isBeforeDate';
import { ModalKeys } from '~/constants/modal';
import { useDispatch, useSelector } from 'react-redux';
import { ReduxState } from '~/types/redux';
import { removeQuote } from '~/redux/actions/common';
import { trackEvent } from '~/helpers/tracking';
import { setNewRelicCustomAttribute } from '~/helpers/newrelic';
import { useState, useEffect, useRef } from 'react';
import clientSide from '~/helpers/clientSide';
import { useModal } from '~/hooks/useModal';
import useReturnVisitorOfferEligibility from '~/hooks/useReturnVisitorOfferEligibility';
import useGlobalOfferMerchData from '~/hooks/useGlobalOfferMerchData';
import { hasValidReturnVisitorCookie, getReturnVisitorCookie, wasRVOShownOnLandingPage } from '~/helpers/returnVisitorCookie';

type ResumeQuoteCheckReturnType = {
  isQuoteValid: boolean;
  hasDeclinedResume: boolean;
  hasCompletedResume: boolean;
  hasEnteredNGCQuiz: boolean;
};

export const checkResumeQuoteStatus = (
  quoteId: string | undefined,
  cartQuoteCutoffDate: string | undefined,
  resume: { acceptedResume?: boolean; completed?: boolean; viewedModal?: boolean } | undefined,
  landingPage: boolean | undefined,
  acp: boolean,
  cookiePass: boolean
): ResumeQuoteCheckReturnType => {
  // Check if user has a quote eligible for resuming
  const hasQuoteId = Boolean(quoteId || Cookies.get('quoteId'));
  const isQuoteValid = hasQuoteId && isBeforeDate(Cookies.get('quoteCreated'), cartQuoteCutoffDate);

  const hasDeclinedResume =
    Boolean(window?.sessionStorage?.getItem('declinedResume')) ||
    Boolean(landingPage) ||
    cookiePass ||
    acp ||
    resume?.acceptedResume === false;
  const hasCompletedResume = resume?.completed === true;

  const hasEnteredNGCQuiz = Boolean(window.sessionStorage.getItem('hasEnteredNGCQuiz'))

  return {
    isQuoteValid,
    hasDeclinedResume,
    hasCompletedResume,
    hasEnteredNGCQuiz
  };
};

// Custom hook for resume quote logic
export const useResumeQuoteCheck = (): { activeModal: ModalKeys | null } => {
  const [hasRemovedCookie, setHasRemovedCookie] = useState(false);
  const hasShownRVOInSessionRef = useRef(false);
  const hasShownResumeModalInSessionRef = useRef(false);
  const {
    resume,
    monarch,
    landingPage,
    acp,
    cookiePass,
    tracking: { quoteId },
  } = useSelector((store: ReduxState) => store);
  const { determineResumeQuote, cartQuoteCutoffDate } = monarch;
  const dispatch = useDispatch();
  const { activeModal, openModal } = useModal();

  // Check Return Visitor Offer eligibility
  const returnVisitorOfferEligibility = useReturnVisitorOfferEligibility();
  const offerMerchandising = useGlobalOfferMerchData();

  useEffect(() => {
    if (clientSide && determineResumeQuote === true) {
      const { isQuoteValid, hasDeclinedResume, hasCompletedResume, hasEnteredNGCQuiz } = checkResumeQuoteStatus(
        quoteId,
        cartQuoteCutoffDate,
        resume,
        Boolean(landingPage),
        Boolean(acp && Object.keys(acp).length > 0),
        Boolean(cookiePass)
      );

      // Check if Return Visitor Offer should be shown instead
      const shouldShowReturnVisitorOffer = () => {
        const returnVisitorCookie = getReturnVisitorCookie();
        const hasValidCookie = hasValidReturnVisitorCookie();
        const hasReturnVisitorMerchandising = Boolean(offerMerchandising?.data?.returnVisitorOffer);
        const isEligibilityTrue = returnVisitorOfferEligibility === true;

        // Don't show modal if it was already shown on the MLP's in THIS session
        // MLP's set sessionStorage flag 'rvoShownOnLandingPage' when modal is displayed
        const shownOnLPThisSession = wasRVOShownOnLandingPage();

        const shouldShow = hasValidCookie && isEligibilityTrue && hasReturnVisitorMerchandising && !hasShownRVOInSessionRef.current && returnVisitorCookie && !shownOnLPThisSession;

        return shouldShow;
      };

      if (activeModal !== null) {
        return;
      }

      // Check if Return Visitor Offer should be shown (independent of resume status)
      if (shouldShowReturnVisitorOffer()) {
        openModal(ModalKeys.RETURN_OFFER);
        hasShownRVOInSessionRef.current = true;
      }
      // Only show resume modal if user is NOT eligible for RVO and has valid resume quote
      // Also check that we haven't already shown it or it was declined
      else if (
        returnVisitorOfferEligibility !== true &&
        isQuoteValid &&
        !hasDeclinedResume &&
        !hasCompletedResume &&
        !hasEnteredNGCQuiz &&
        !hasShownResumeModalInSessionRef.current
      ) {
        openModal(ModalKeys.RESUME);
        hasShownResumeModalInSessionRef.current = true;
        trackEvent({
          action: 'elementViewed',
          data: {
            elementType: 'MODAL',
            location: 'ADDRESS CHECK',
            position: 'Resume Modal',
            text: 'Resume Modal ',
          },
        });
      }

      if (hasDeclinedResume) {
        setNewRelicCustomAttribute([{ name: 'declinedResume', value: 'true' }]);
      }

      // Clear expired quotes
      if (
        !isBeforeDate(Cookies.get('quoteCreated'), cartQuoteCutoffDate) &&
        Cookies.get('quoteId') &&
        !hasRemovedCookie
      ) {
        setNewRelicCustomAttribute([{ name: 'cartQuoteCutoffDate', value: Cookies.get('quoteId') }]);
        dispatch(removeQuote());
        setHasRemovedCookie(true);
        window.sessionStorage.setItem('declinedResume', 'true');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [determineResumeQuote, returnVisitorOfferEligibility, offerMerchandising?.data?.returnVisitorOffer]);

  return { activeModal };
};
